import { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "lifebutton_cart";

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const addItem = (product, qty = 1) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.lookup_key === product.lookup_key);
      if (existing) {
        return prev.map((i) =>
          i.lookup_key === product.lookup_key ? { ...i, quantity: i.quantity + qty } : i
        );
      }
      return [
        ...prev,
        {
          lookup_key: product.lookup_key,
          name: product.name,
          price: product.price,
          image: product.image,
          category: product.category,
          quantity: qty,
        },
      ];
    });
  };

  const removeItem = (lookup_key) =>
    setItems((prev) => prev.filter((i) => i.lookup_key !== lookup_key));

  const updateQty = (lookup_key, quantity) =>
    setItems((prev) =>
      prev
        .map((i) => (i.lookup_key === lookup_key ? { ...i, quantity } : i))
        .filter((i) => i.quantity > 0)
    );

  const clearCart = () => setItems([]);

  const count = useMemo(() => items.reduce((s, i) => s + i.quantity, 0), [items]);
  const subtotal = useMemo(
    () => items.reduce((s, i) => s + i.price * i.quantity, 0),
    [items]
  );

  const value = { items, addItem, removeItem, updateQty, clearCart, count, subtotal };
  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
